import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import { Close, OpenInNew, ContentCopy } from '@mui/icons-material';
import SamPreview from './SamPreview';
import { formatGermanDateTime } from '../../helpers/dateFuncs';
import { formatNumber } from '../../helpers/numberUtils';
import { num, SamData } from './samTimelineTypes';

// Detailansicht eines Timeline-Eintrags (Webinar-/PLX-Mailing ueber Iterable).
// Kennzahlen aus GET /api/sam-details/:campaignId — dieselben Definitionen wie die Timeline.

interface SamDetails {
  sam_details: any;
  has_preview: boolean;
}

interface SamDetailModalProps {
  open: boolean;
  onClose: () => void;
  sam: SamData | null;
}

function TabPanel({ children, value, index, ...other }: any) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sam-tabpanel-${index}`}
      aria-labelledby={`sam-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Metric: React.FC<{ value: React.ReactNode; label: string; color?: string }> = ({
  value,
  label,
  color,
}) => (
  <Box textAlign="center">
    <Typography variant="h5" color={color}>
      {value}
    </Typography>
    <Typography variant="body2">{label}</Typography>
  </Box>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="subtitle2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body1">{value}</Typography>
  </Box>
);

const eur = (v: unknown) => `${formatNumber(num(v), 2)} €`;
const pct = (v: unknown) => `${formatNumber(num(v), 2)} %`;

const SamDetailModal: React.FC<SamDetailModalProps> = ({ open, onClose, sam }) => {
  const [tabValue, setTabValue] = useState(0);
  const [details, setDetails] = useState<SamDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sam) return;
    let cancelled = false;
    setDetails(null);
    setTabValue(0);
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const response = await fetch(
          `/api/sam-details/${encodeURIComponent(sam.campaign_id)}?type=${sam.campaign_type}`,
        );
        if (!response.ok) throw new Error('Details konnten nicht geladen werden');
        const result = await response.json();
        if (!cancelled) setDetails(result);
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sam]);

  if (!sam) return null;

  const d = details?.sam_details ?? {};
  const isWebinar = sam.campaign_type === 'webinar_mailing';
  const anfos = num(sam.anfos);
  const cpaRevenue = num(sam.cpa_revenue);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="div">
            {isWebinar ? 'Webinar-Mailing' : 'PLX-Mailing'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sam.subject}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Kampagnen-ID kopieren">
            <IconButton onClick={() => navigator.clipboard.writeText(sam.campaign_id)} size="small">
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <Tooltip title="In Iterable öffnen">
            <IconButton
              onClick={() =>
                window.open(`https://app.iterable.com/campaigns/${sam.campaign_id}`, '_blank')
              }
              size="small"
            >
              <OpenInNew />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_e, v) => setTabValue(v)}>
            <Tab label="Übersicht" />
            <Tab label="Mailing-Info" />
            <Tab label="Vorschau" disabled={!details?.has_preview} />
            <Tab label="Leistung" />
            <Tab label="Rohdaten" />
          </Tabs>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {details && !loading && (
          <>
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Kampagne
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Kampagnen-ID:</Typography>
                          <Typography variant="body2" fontFamily="monospace">
                            {sam.campaign_id}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Versand:</Typography>
                          <Typography variant="body2">
                            {formatGermanDateTime(sam.send_time)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Typ:</Typography>
                          <Chip
                            label={isWebinar ? 'WEBINAR' : 'PLX'}
                            color={isWebinar ? 'info' : 'error'}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">ESP:</Typography>
                          <Chip label="ITERABLE" color="success" size="small" />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Kennzahlen
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Metric
                            value={formatNumber(num(sam.recipients), 0)}
                            label="Empfänger"
                            color="primary"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Metric value={formatNumber(anfos, 0)} label="Anfos" color="secondary" />
                        </Grid>
                        <Grid item xs={6}>
                          <Metric value={pct(sam.open_rate)} label="Öffnungsrate" />
                        </Grid>
                        <Grid item xs={6}>
                          <Metric value={pct(sam.click_rate)} label="Klickrate" />
                        </Grid>
                        <Grid item xs={6}>
                          <Metric value={pct(sam.anfo_rate)} label="Anfo-Rate" />
                        </Grid>
                        <Grid item xs={6}>
                          <Metric value={eur(sam.etkp)} label="ETKP (CPA-Wert je 1.000)" />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Umsatz
                      </Typography>
                      <Box display="flex" justifyContent="space-around" alignItems="center">
                        <Metric
                          value={eur(cpaRevenue)}
                          label="CPA-Wert (Bewertung der Anfos)"
                          color="success.main"
                        />
                        <Metric
                          value={eur(sam.actual_revenue)}
                          label="Umsatz (bezahlt)"
                          color="info.main"
                        />
                        <Metric
                          value={anfos > 0 ? eur(cpaRevenue / anfos) : '—'}
                          label="CPA-Wert je Anfo"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        E-Mail
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        <Field label="Betreff" value={d.subject || '—'} />
                        <Field label="Absendername" value={d.from_name || '—'} />
                        <Field label="Absenderadresse" value={d.from_email || '—'} />
                        {d.preview_text ? <Field label="Preheader" value={d.preview_text} /> : null}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Versandplan
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        <Field
                          label="Mandant"
                          value={(d.webinar_client_code || d.plx_client_code || '—').toUpperCase()}
                        />
                        <Field
                          label="Status"
                          value={(d.webinar_status || d.plx_status || 'unbekannt').toUpperCase()}
                        />
                        <Field
                          label="Versendet"
                          value={formatGermanDateTime(
                            d.webinar_sent_at || d.plx_sent_at || sam.send_time,
                          )}
                        />
                        <Field label="Iterable-Status" value={d.campaign_state || '—'} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <SamPreview campaignId={sam.campaign_id} campaignType={sam.campaign_type} />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Leistung im Detail
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Engagement</Typography>
                      <Box mt={1}>
                        <Typography>
                          Öffnungen (gesamt): {formatNumber(num(sam.opens), 0)}
                        </Typography>
                        <Typography>
                          Eindeutige Öffner: {formatNumber(num(sam.unique_opens), 0)}
                        </Typography>
                        <Typography>
                          Eindeutige Klicker: {formatNumber(num(sam.clicks), 0)}
                        </Typography>
                        <Typography>Anfos: {formatNumber(anfos, 0)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Raten</Typography>
                      <Box mt={1}>
                        <Typography>Öffnungsrate: {pct(sam.open_rate)}</Typography>
                        <Typography>Klickrate: {pct(sam.click_rate)}</Typography>
                        <Typography>Anfo-Rate: {pct(sam.anfo_rate)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">Bounces & Abmeldungen</Typography>
                      <Box mt={1}>
                        <Typography>Bounces: {formatNumber(num(d.hard_bounces), 0)}</Typography>
                        <Typography>Abmeldungen: {formatNumber(num(d.unsubscribed), 0)}</Typography>
                        <Typography>Beschwerden: {formatNumber(num(d.complaints), 0)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                  {JSON.stringify({ row: sam, details }, null, 2)}
                </pre>
              </Paper>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SamDetailModal;
